import { Input, Select, Textarea } from '@/components/ui/FormControls';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import Link from '@/components/common/AccessLink';
import {
  usePurchase,
  usePurchaseOperation,
  usePurchaseWorkspace,
  usePurchaseReceipts,
  usePurchasePeople,
  type PurchaseDetail,
} from '@/hooks/usePurchases';
import { useProducts } from '@/hooks/useProducts';
import { useAuth } from '@/contexts/AuthContext';
import { useConfirm } from '@/contexts/ConfirmContext';
import { usePurchaseIntent } from '@/hooks/usePurchaseIntent';
import PageHeader from '@/components/common/PageHeader';
import LoadingState from '@/components/common/LoadingState';
import ErrorState from '@/components/common/ErrorState';
import StatusBadge from '@/components/common/StatusBadge';
import ExportActions from '@/components/common/ExportActions';
import WorkModalShell from '@/components/common/WorkModalShell';
import PurchaseOrderPreview from '@/components/purchases/PurchaseOrderPreview';
import PurchasePayments from '@/components/purchases/PurchasePayments';
import { purchaseInputClass as input } from '@/components/purchases/PurchasePaymentFields';
import { purchaseTotals, purchaseVariantDetails, saleMoney } from '@klothiq/domain';
import { localBusinessDate } from '@klothiq/validation';
import { toast } from 'sonner';

type Item = PurchaseDetail['purchase_items'][number];
const itemName = (i: Item) => i.description || i.product_variants?.products?.name || 'Product';
export default function PurchaseDetailPage() {
  const { id } = useParams();
  const { can } = useAuth();
  const confirm = useConfirm();
  const intent = usePurchaseIntent(id || 'detail');
  const query = usePurchase(id);
  const workspace = usePurchaseWorkspace();
  const people = usePurchasePeople(!!workspace.data);
  const receipts = usePurchaseReceipts(id, !!workspace.data);
  const products = useProducts();
  const receive = usePurchaseOperation('receive_purchase');
  const cancel = usePurchaseOperation('cancel_purchase');
  const link = usePurchaseOperation('link_purchase_item');
  const [showOrder, setShowOrder] = useState(false);
  const [quantities, setQuantities] = useState<Record<string, string>>({});
  const [error, setError] = useState('');
  const [dialog, setDialog] = useState<'cancel' | Item | null>(null);
  const [reason, setReason] = useState('');
  const [variant, setVariant] = useState('');
  const busy = receive.isPending || cancel.isPending || link.isPending;
  if (query.isLoading) return <LoadingState message="Loading purchase…" />;
  if (query.error) return <ErrorState message={query.error.message} onRetry={query.refetch} />;
  if (!query.data) return <ErrorState message="Purchase is unavailable" />;
  const p = query.data;
  const totals = purchaseTotals(p.purchase_items, Number(p.other_charges));
  const canReceive =
    !!workspace.data &&
    can('purchases.receive') &&
    !p.is_voided &&
    ['ordered', 'partially_received'].includes(p.status);
  const variants = (products.data || []).flatMap((product) =>
    (product.product_variants || []).map((v) => ({
      id: v.id,
      label: `${product.name} · ${v.sku} (${v.size})`,
    })),
  );
  const exportRows = p.purchase_items.map((i) => ({
    product: itemName(i),
    variant:
      [purchaseVariantDetails(i).size, purchaseVariantDetails(i).color]
        .filter(Boolean)
        .join(' / ') || '—',
    sku: purchaseVariantDetails(i).sku || 'Manual item',
    ordered: i.quantity,
    received: i.received_quantity,
    cost: Number(i.unit_cost),
    discount: Number(i.discount),
    total: Number(i.line_total),
  }));
  const overdue =
    p.expected_date &&
    p.expected_date < localBusinessDate() &&
    ['ordered', 'partially_received'].includes(p.status);
  async function receiving(all: boolean) {
    setError('');
    const items = p.purchase_items
      .filter((i) => i.inventory_treatment !== 'link_required')
      .map((i) => ({
        purchase_item_id: i.id,
        received_quantity: all ? i.quantity - i.received_quantity : Number(quantities[i.id] || 0),
      }))
      .filter((i) => i.received_quantity !== 0);
    if (
      !items.length ||
      items.some(
        (i) =>
          !Number.isInteger(i.received_quantity) ||
          i.received_quantity <= 0 ||
          i.received_quantity >
            p.purchase_items.find((x) => x.id === i.purchase_item_id)!.quantity -
              p.purchase_items.find((x) => x.id === i.purchase_item_id)!.received_quantity,
      )
    ) {
      setError('Enter whole quantities within each pending quantity.');
      return;
    }
    if (
      !(await confirm({
        title: 'Confirm receipt',
        description: `Record ${items.reduce((s, i) => s + i.received_quantity, 0)} units? Tracked products increase stock; non-stock items record fulfillment only. Items awaiting a product link are excluded.`,
        confirmText: 'Confirm receipt',
      }))
    )
      return;
    const payload = { purchase_id: p.id, expected_updated_at: p.updated_at, items };
    try {
      await receive.mutateAsync({ ...payload, request_key: intent.key('receive', payload) });
      intent.complete('receive');
      setQuantities({});
      toast.success('Receipt recorded');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Receipt could not be recorded');
    }
  }
  async function recover(operation: string) {
    try {
      const prior = await intent.recover(operation);
      if (prior) {
        setDialog(null);
        setQuantities({});
        await query.refetch();
        await receipts.refetch();
        setError('');
        toast.info('The previous request completed. Details refreshed.');
      } else toast.info('No completed request found. Retry the original action.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not check the previous request');
    }
  }
  async function dialogSave() {
    if (!dialog) return;
    setError('');
    if (reason.trim().length < 3 || reason.trim().length > 500) {
      setError('Enter a reason (3–500 characters).');
      return;
    }
    const payload = {
      purchase_id: p.id,
      expected_updated_at: p.updated_at,
      reason: reason.trim(),
      ...(dialog === 'cancel' ? {} : { purchase_item_id: dialog.id, variant_id: variant }),
    };
    try {
      await (dialog === 'cancel' ? cancel : link).mutateAsync({
        ...payload,
        request_key: intent.key('purchase-action', payload),
      });
      intent.complete('purchase-action');
      setDialog(null);
      toast.success(
        dialog === 'cancel'
          ? 'Purchase cancelled'
          : 'Product linked; stock is added only when received',
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Purchase could not be updated');
    }
  }
  return (
    <div className="space-y-5 pb-6">
      <PageHeader
        title={`Purchase ${p.purchase_number}`}
        description={p.suppliers?.name || 'Supplier'}
        backTo="/purchases"
        actions={
          <div className="flex flex-wrap gap-2">
            <ExportActions
              onViewReceipt={() => setShowOrder(true)}
              onPrint={() => setShowOrder(true)}
              excelOptions={() => ({
                fileName: `PO_${p.purchase_number}`,
                sheetName: 'PurchaseOrder',
                columns: [
                  { header: 'Product', key: 'product' },
                  { header: 'SKU', key: 'sku' },
                  { header: 'Variant', key: 'variant' },
                  { header: 'Ordered', key: 'ordered', format: 'integer' },
                  { header: 'Received', key: 'received', format: 'integer' },
                  { header: 'Unit cost', key: 'cost', format: 'currency' },
                  { header: 'Discount', key: 'discount', format: 'currency' },
                  { header: 'Total', key: 'total', format: 'currency' },
                ],
                data: exportRows,
              })}
              pdfOptions={() => ({
                fileName: `PO_${p.purchase_number}`,
                title: `PURCHASE ORDER — ${p.purchase_number}`,
                subtitle: `Supplier: ${p.suppliers?.name || '—'} · Date: ${p.purchase_date}${p.supplier_invoice_no ? ' · Invoice: ' + p.supplier_invoice_no : ''}`,
                columns: [
                  { header: 'Product', key: 'product' },
                  { header: 'Variant', key: 'variant' },
                  { header: 'Qty', key: 'ordered', format: 'integer' },
                  { header: 'Unit cost', key: 'cost', format: 'currency' },
                  { header: 'Discount', key: 'discount', format: 'currency' },
                  { header: 'Total', key: 'total', format: 'currency' },
                ],
                rows: exportRows,
                summaryLines: [
                  { label: 'Subtotal', value: saleMoney(totals.subtotal) },
                  { label: 'Total discount', value: saleMoney(totals.discount) },
                  { label: 'Other charges', value: saleMoney(p.other_charges) },
                  { label: 'Total', value: saleMoney(p.total_amount) },
                  { label: 'Paid', value: saleMoney(p.paid_amount) },
                  { label: 'Balance', value: saleMoney(p.balance_due) },
                  { label: 'Document', value: 'Purchase order — not proof of payment' },
                ],
              })}
            />
            {can('purchases.edit') &&
              !p.is_voided &&
              ['draft', 'ordered', 'partially_received', 'received'].includes(p.status) && (
                <Link
                  to={`/purchases/${p.id}/edit`}
                  className="inline-flex min-h-11 items-center rounded-xl border border-border px-4 text-sm"
                >
                  {['partially_received', 'received'].includes(p.status)
                    ? 'Edit details'
                    : 'Edit purchase'}
                </Link>
              )}
            {!!workspace.data &&
              can('purchases.cancel') &&
              !p.is_voided &&
              ['draft', 'ordered'].includes(p.status) && (
                <button
                  onClick={() => {
                    setDialog('cancel');
                    setReason('');
                    setError('');
                  }}
                  className="min-h-11 rounded-xl border border-border px-4 text-sm"
                >
                  Cancel order
                </button>
              )}
          </div>
        }
      />
      {workspace.data === false && (
        <p
          role="alert"
          className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm"
        >
          Purchase updates require the purchase database migration. Existing details and payment
          history remain available.
        </p>
      )}
      {workspace.error && (
        <ErrorState message={workspace.error.message} onRetry={workspace.refetch} />
      )}
      {workspace.isLoading && (
        <p role="status" className="text-sm text-muted-foreground">
          Checking purchase service…
        </p>
      )}
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <StatusBadge status={p.status} />
        <StatusBadge status={p.payment_status} />
        <span className="rounded-full border border-border px-3 py-1 text-xs">
          {p.entry_mode === 'direct' ? 'Direct entry' : 'Catalog purchase'}
        </span>
        <span className="text-muted-foreground">{p.purchase_date}</span>
        <span className="text-muted-foreground">
          Created by{' '}
          {people.data?.find((person) => person.id === p.created_by)?.full_name || 'Recorded user'}
        </span>
        {p.expected_date && (
          <span className={overdue ? 'text-amber-600' : 'text-muted-foreground'}>
            {overdue ? 'Delivery overdue · ' : 'Expected · '}
            {p.expected_date}
          </span>
        )}
        {p.supplier_invoice_no && (
          <span className="break-all text-muted-foreground">Invoice: {p.supplier_invoice_no}</span>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {[
          ['Subtotal', totals.subtotal],
          ['Total discount', totals.discount],
          ['Other charges', Number(p.other_charges)],
          ['Total', Number(p.total_amount)],
          ['Paid', Number(p.paid_amount)],
          ['Balance', Number(p.balance_due)],
        ].map(([label, value]) => (
          <div key={String(label)} className="min-w-0 rounded-xl border border-border bg-card p-3">
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className="mt-1 break-words text-base font-semibold">{saleMoney(Number(value))}</p>
          </div>
        ))}
      </div>
      <section className="space-y-4 rounded-2xl border border-border bg-card p-4 sm:p-5">
        <p className="text-xs text-muted-foreground">
          Stock cost uses the entered unit cost. Discounts and other charges are not allocated to
          stock cost.
        </p>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-semibold">Items & receiving</h2>
          {canReceive && (
            <button
              disabled={busy}
              onClick={() => void receiving(true)}
              className="min-h-11 rounded-xl border border-border px-4 text-sm"
            >
              Receive all eligible
            </button>
          )}
        </div>
        <div className="grid gap-3 lg:grid-cols-2">
          {p.purchase_items.map((item) => {
            const pending = item.quantity - item.received_quantity;
            return (
              <article
                key={item.id}
                className="min-w-0 space-y-3 rounded-xl border border-border p-4"
              >
                <div>
                  <h3 className="break-words font-semibold">{itemName(item)}</h3>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {[
                      purchaseVariantDetails(item).sku,
                      purchaseVariantDetails(item).size,
                      purchaseVariantDetails(item).color,
                    ]
                      .filter(Boolean)
                      .join(' · ') || 'Manual item'}{' '}
                    ·{' '}
                    {item.inventory_treatment === 'tracked'
                      ? 'Stock tracked'
                      : item.inventory_treatment === 'non_stock'
                        ? 'No stock tracking'
                        : 'Product link needed'}
                  </p>
                </div>
                <dl className="grid grid-cols-3 gap-3 text-sm">
                  {[
                    ['Ordered', item.quantity],
                    ['Received', item.received_quantity],
                    ['Pending', pending],
                  ].map(([label, value]) => (
                    <div key={String(label)}>
                      <dt className="text-xs text-muted-foreground">{label}</dt>
                      <dd className="mt-1 font-semibold">{value}</dd>
                    </div>
                  ))}
                </dl>
                <div className="flex flex-wrap justify-between gap-2 text-xs text-muted-foreground">
                  <span>Unit cost {saleMoney(item.unit_cost)}</span>
                  <span>Discount {saleMoney(item.discount)}</span>
                  <span className="font-semibold text-foreground">
                    Total {saleMoney(item.line_total)}
                  </span>
                </div>
                {canReceive && pending > 0 && item.inventory_treatment !== 'link_required' && (
                  <label className="block space-y-1 text-sm" htmlFor={`receive-${item.id}`}>
                    <span>Receive now</span>
                    <Input
                      id={`receive-${item.id}`}
                      type="number"
                      min={0}
                      max={pending}
                      step={1}
                      value={quantities[item.id] || ''}
                      onChange={(e) => setQuantities({ ...quantities, [item.id]: e.target.value })}
                      disabled={busy}
                      placeholder={`Up to ${pending}`}
                      className={input}
                      helpLabel="Receive now"
                    />
                  </label>
                )}
                {!!workspace.data &&
                  can('purchases.edit') &&
                  !p.is_voided &&
                  can('purchases.receive') &&
                  item.inventory_treatment === 'link_required' &&
                  pending > 0 &&
                  ['draft', 'ordered', 'partially_received'].includes(p.status) && (
                    <button
                      onClick={() => {
                        setDialog(item);
                        setVariant('');
                        setReason('');
                        setError('');
                      }}
                      className="min-h-11 rounded-xl border border-border px-3 text-sm"
                    >
                      Link product to receive stock
                    </button>
                  )}
              </article>
            );
          })}
        </div>
        {canReceive && (
          <div className="flex justify-end">
            <button
              disabled={busy}
              onClick={() => void receiving(false)}
              className="min-h-11 rounded-xl bg-primary px-4 text-sm font-semibold text-primary-foreground disabled:opacity-50"
            >
              {receive.isPending ? 'Recording…' : 'Receive selected quantities'}
            </button>
          </div>
        )}
        {error && !dialog && (
          <p role="alert" className="rounded-xl bg-destructive/10 p-3 text-sm text-destructive">
            {error}
            <button
              disabled={busy}
              onClick={() => void recover('receive')}
              className="ml-2 min-h-11 underline"
            >
              Check previous receipt
            </button>
          </p>
        )}
      </section>
      {showOrder && <PurchaseOrderPreview purchase={p} onClose={() => setShowOrder(false)} />}
      <PurchasePayments purchase={p} enabled={!!workspace.data && !p.is_voided} />
      {!!workspace.data && (
        <section className="space-y-3 rounded-2xl border border-border bg-card p-4">
          <h2 className="font-semibold">Receipt history</h2>
          {receipts.isLoading && <p className="text-sm text-muted-foreground">Loading receipts…</p>}
          {receipts.error && (
            <p role="alert" className="text-sm text-destructive">
              {receipts.error.message}{' '}
              <button className="underline" onClick={() => void receipts.refetch()}>
                Retry
              </button>
            </p>
          )}
          {receipts.data?.map((r) => (
            <div key={r.id} className="space-y-2 rounded-xl border border-border p-3">
              <p className="text-sm font-medium">
                {new Date(r.created_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
              </p>
              {r.purchase_receipt_items.map((i) => (
                <p key={i.purchase_item_id} className="text-sm text-muted-foreground">
                  {p.purchase_items.find((x) => x.id === i.purchase_item_id)
                    ? itemName(p.purchase_items.find((x) => x.id === i.purchase_item_id)!)
                    : 'Item'}{' '}
                  × {i.quantity}
                  {i.inventory_treatment === 'non_stock' ? ' · No stock change' : ''}
                </p>
              ))}
              <p className="text-xs text-muted-foreground">
                Received by{' '}
                {people.data?.find((person) => person.id === r.created_by)?.full_name ||
                  'Recorded user'}
              </p>
              {r.notes && <p className="text-sm">{r.notes}</p>}
            </div>
          ))}
          {!!receipts.data &&
            p.purchase_items.reduce((sum, i) => sum + i.received_quantity, 0) >
              receipts.data.reduce(
                (sum, r) => sum + r.purchase_receipt_items.reduce((n, i) => n + i.quantity, 0),
                0,
              ) && (
              <p className="text-sm text-muted-foreground">
                The totals above include historical receipts recorded before detailed receipt
                history was available.
              </p>
            )}
          {receipts.data?.length === 0 && (
            <p className="text-sm text-muted-foreground">
              {p.purchase_items.some((i) => i.received_quantity > 0)
                ? 'Historical received quantities are shown above. Detailed receipt batches were not recorded for those receipts.'
                : 'No receipts recorded yet.'}
            </p>
          )}
        </section>
      )}
      {p.notes && (
        <section className="rounded-2xl border border-border bg-card p-4">
          <h2 className="mb-2 font-semibold">Notes</h2>
          <p className="whitespace-pre-wrap break-words text-sm text-muted-foreground">{p.notes}</p>
        </section>
      )}
      <WorkModalShell
        open={!!dialog}
        onClose={() => setDialog(null)}
        title={dialog === 'cancel' ? 'Cancel purchase' : 'Link product'}
        busy={busy}
        dirty={!!reason || !!variant}
      >
        <h2 className="mb-3 text-lg font-semibold">
          {dialog === 'cancel' ? 'Cancel purchase' : 'Link product'}
        </h2>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void dialogSave();
          }}
          className="space-y-4 overflow-y-auto"
        >
          {dialog && dialog !== 'cancel' && (
            <>
              <p className="text-sm text-muted-foreground">
                {itemName(dialog)}. Linking adds no stock until you record receipt.
              </p>
              <label htmlFor="purchase-link" className="block space-y-1 text-sm">
                <span>Product *</span>
                <Select
                  id="purchase-link"
                  value={variant}
                  onChange={(e) => setVariant(e.target.value)}
                  required
                  className={input}
                  helpLabel="Product *"
                >
                  <option value="">Select product variant</option>
                  {variants.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.label}
                    </option>
                  ))}
                </Select>
              </label>
              {products.error && (
                <p role="alert" className="text-sm text-destructive">
                  Products could not load.{' '}
                  <button
                    type="button"
                    onClick={() => void products.refetch()}
                    className="underline"
                  >
                    Retry
                  </button>
                </p>
              )}
            </>
          )}
          {dialog === 'cancel' && (
            <p className="text-sm text-muted-foreground">
              Only unreceived orders without active payments can be cancelled. History is retained.
            </p>
          )}
          <label htmlFor="purchase-reason" className="block space-y-1 text-sm">
            <span>Reason *</span>
            <Textarea
              id="purchase-reason"
              required
              minLength={3}
              maxLength={500}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className={input}
              helpLabel="Reason *"
            />
          </label>
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
              <button
                type="button"
                disabled={busy}
                onClick={() => void recover('purchase-action')}
                className="ml-2 min-h-11 underline"
              >
                Check previous request
              </button>
            </p>
          )}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => setDialog(null)}
              className="min-h-11 rounded-xl border border-border px-4 text-sm"
            >
              Close
            </button>
            <button
              disabled={busy}
              className="min-h-11 rounded-xl bg-primary px-4 text-sm font-semibold text-primary-foreground"
            >
              {busy ? 'Saving…' : 'Confirm'}
            </button>
          </div>
        </form>
      </WorkModalShell>
    </div>
  );
}
